/* eslint-disable consistent-return, import/no-extraneous-dependencies, import/no-named-as-default, import/extensions, import/no-unresolved, sort-keys */
import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import Tooltip from 'react-tt';

import DatePicker from 'wanderu-component-datepicker';
import { localize } from 'wanderu-i18n';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './RoundTripDatePicker.scss';
import variables from 'wanderu-css/variables.scss';
const {
    tooltipZIndex,
    tooltipSpacing,
} = variables;

const parsedTooltipZIndex = parseInt(tooltipZIndex, 10) || 0;
const parsedTooltipSpacing = parseInt(tooltipSpacing, 10) || 10;

function RoundTripDatePicker(props) {
    const {
        t,
        locale,
        cellClass,
        departData,
        departDate,
        highlightClass,
        i18nMoment,
        minDate,
        onDepartChange,
        onReturnChange,
        onFocus,
        returnData,
        returnDate,
        rowClass,
        missingDepartDate,
        missingReturnDate,
        isRoundTrip,
        showTooltips = true,
        loading,
        searchType,
    } = props;

    function onDepartDateChange(e) {
        const onDepartDate = e.currentTarget.value;

        if (!onDepartDate) {
            return onDepartChange(undefined);
        }

        if (returnDate && onDepartDate.isAfter(returnDate, 'day')) {
            onReturnChange(onDepartDate.format('YYYY-MM-DD'));
        }
        onDepartChange(onDepartDate.format('YYYY-MM-DD'));
    }

    function onReturnDateChange(e) {
        const onReturnDate = e.currentTarget.value;

        if (!onReturnDate) {
            return onReturnChange(undefined);
        }

        if (departDate && onReturnDate.isBefore(departDate, 'day')) {
            onDepartChange(onReturnDate.format('YYYY-MM-DD'));
        }

        onReturnChange(onReturnDate.format('YYYY-MM-DD'));
    }

    const momentDepartDate = departDate && i18nMoment(departDate);
    const momentReturnDate = returnDate && i18nMoment(returnDate);
    const momentMinDate = minDate && i18nMoment(minDate);

    return (
        <span className={rowClass}>
            <div
                className={classNames(
                    cellClass,
                    styles.cell,
                    styles.inputContainer,
                )}
            >
                <DatePicker
                    calendarClass={styles.departCalendarContainer}
                    containerClass={styles.datePickerContainer}
                    currentDate={momentDepartDate}
                    dateData={departData}
                    highlightClass={highlightClass}
                    iconClass={styles.icon}
                    inputClass={classNames(
                        'departDatePicker',
                        styles.datePickerInput,
                        {
                            [styles.withRoundTrip]: isRoundTrip,
                            [styles.highlight]: !showTooltips && missingDepartDate,
                        },
                    )}
                    loading={loading}
                    locale={locale}
                    minDate={momentMinDate}
                    onChange={onDepartDateChange}
                    onFocus={() => onFocus('missingDepartDate')}
                    placeholder={t('departDate placeholder')}
                    searchType={searchType}
                    today={t('today')}
                    required
                    showPin
                />

                {showTooltips && missingDepartDate && (
                    <div className={styles.tooltip}>
                        <Tooltip
                            id="depart-date-tooltip"
                            location="bottom"
                            spacing={parsedTooltipSpacing}
                            zIndex={parsedTooltipZIndex}
                            sticky
                        >
                            <div className={styles.tooltipContent}>
                                {t('pickDateTooltip')}
                            </div>
                        </Tooltip>
                    </div>
                )}

            </div>
            {isRoundTrip && (
                <>
                    <div
                        className={classNames(
                            styles.divider,
                            { [styles.hide]: !isRoundTrip },
                        )}
                    />

                    <div
                        className={classNames(
                            cellClass,
                            styles.cell,
                            styles.inputContainer,
                            { [styles.hide]: !isRoundTrip },
                        )}
                    >
                        <DatePicker
                            calendarClass={styles.returnCalendarContainer}
                            containerClass={styles.datePickerContainer}
                            currentDate={momentReturnDate}
                            dateData={returnData}
                            highlightClass={highlightClass}
                            iconClass={styles.icon}
                            inputClass={classNames(
                                'returnDatePicker',
                                styles.datePickerInput,
                                {
                                    [styles.roundTrip]: isRoundTrip,
                                    [styles.highlight]: !showTooltips && missingReturnDate,
                                },
                            )}
                            loading={loading}
                            locale={locale}
                            minDate={momentDepartDate || momentMinDate}
                            onChange={onReturnDateChange}
                            onFocus={() => onFocus('missingReturnDate')}
                            placeholder={t('returnDate placeholder')}
                            searchType={searchType}
                            today={t('today')}
                            required
                            showPin
                        />

                        {showTooltips && missingReturnDate && (
                            <div className={styles.tooltip}>
                                <Tooltip
                                    id="return-date-tooltip"
                                    location="bottom"
                                    spacing={parsedTooltipSpacing}
                                    zIndex={parsedTooltipZIndex}
                                    sticky
                                >
                                    <div className={styles.tooltipContent}>
                                        {t('pickDateTooltip')}
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </>
            )}

        </span>
    );
}

RoundTripDatePicker.propTypes = {
    locale: PropTypes.string.isRequired,
    minDate: PropTypes.string,
    departDate: PropTypes.string,
    returnDate: PropTypes.string,
    departData: PropTypes.object,
    returnData: PropTypes.object,
    onDepartChange: PropTypes.func,
    onReturnChange: PropTypes.func,
    rowClass: PropTypes.string,
    cellClass: PropTypes.string,
    missingDepartDate: PropTypes.bool,
    missingReturnDate: PropTypes.bool,
    isRoundTrip: PropTypes.bool,
};

export {
    RoundTripDatePicker,
};

export default localize(RoundTripDatePicker, ['t', 'i18nMoment'], 'widget');
